import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import ImageUpload from './ImageUpload';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X,
  Package,
  Star,
  TrendingDown
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'smartphone_neuf',
    description: '',
    price: '',
    original_price: '',
    image_url: '',
    images: [],
    stock: 0,
    is_featured: false,
    specs: {}
  });

  const categories = [
    { value: 'smartphone_neuf', label: 'Smartphone Neuf' },
    { value: 'smartphone_reconditionne', label: 'Smartphone Reconditionné' },
    { value: 'pc_portable', label: 'PC Portable' },
    { value: 'accessoire', label: 'Accessoire' },
    { value: 'abonnement_voo', label: 'Abonnement VOO' },
    { value: 'abonnement_digi', label: 'Abonnement DIGI-BELGIUM' },
    { value: 'abonnement_hey', label: 'Abonnement Hey Télécom' }
  ];

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products?is_active=true`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'smartphone_neuf',
      description: '',
      price: '',
      original_price: '',
      image_url: '',
    images: [],
      stock: 0,
      is_featured: false,
      specs: {}
    });
    setEditingProduct(null);
    setShowForm(false);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      category: product.category,
      description: product.description,
      price: product.price,
      original_price: product.original_price || '',
      image_url: product.image_url || '',
      stock: product.stock,
      is_featured: product.is_featured,
      specs: product.specs || {}
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        stock: parseInt(formData.stock)
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productData);
        toast.success('Produit mis à jour avec succès');
      } else {
        await axios.post(`${API}/products`, productData);
        toast.success('Produit créé avec succès');
      }
      
      fetchProducts();
      resetForm();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Erreur lors de l\'enregistrement du produit');
    }
  };

  const handleDelete = async (productId, productName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${productName}" ?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success('Produit supprimé avec succès');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const getCategoryLabel = (category) => {
    return categories.find(c => c.value === category)?.label || category;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <Package className="w-8 h-8" />
            Gestion des Produits
          </h1>
          <p className="text-gray-600 mt-2">
            Gérez votre catalogue de produits et promotions
          </p>
        </div>
        
        {!showForm && (
          <Button onClick={() => setShowForm(true)} size="lg">
            <Plus className="w-5 h-5 mr-2" />
            Nouveau Produit
          </Button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <Card className="mb-6 border-2 border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{editingProduct ? 'Modifier le Produit' : 'Nouveau Produit'}</span>
              <Button variant="ghost" size="sm" onClick={resetForm}>
                <X className="w-4 h-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Nom du produit *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="iPhone 14 Pro 128GB"
                  />
                </div>

                <div>
                  <Label htmlFor="category">Catégorie *</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  className="w-full min-h-[100px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Description détaillée du produit..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="price">Prix (€) *</Label>
                  <Input
                    id="price"
                    name="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                    placeholder="299.99"
                  />
                </div>

                <div>
                  <Label htmlFor="original_price">Prix original (€)</Label>
                  <Input
                    id="original_price"
                    name="original_price"
                    type="number"
                    step="0.01"
                    value={formData.original_price}
                    onChange={handleInputChange}
                    placeholder="399.99"
                  />
                  <p className="text-xs text-gray-500 mt-1">Pour afficher une réduction</p>
                </div>

                <div>
                  <Label htmlFor="stock">Stock</Label>
                  <Input
                    id="stock"
                    name="stock"
                    type="number"
                    value={formData.stock}
                    onChange={handleInputChange}
                    placeholder="10"
                  />
                </div>
              </div>

              <div>
              <div>
                <Label>Images du Produit (Upload)</Label>
                <p className="text-sm text-gray-500 mb-2">Upload jusquà 5 images</p>
                <ImageUpload
                  multiple={true}
                  maxFiles={5}
                  currentImages={formData.images || []}
                  onImageUploaded={(images) => setFormData({ ...formData, images })}
                />
              </div>


                <Label htmlFor="image_url">URL de l'image</Label>
                <Input
                  id="image_url"
                  name="image_url"
                  type="url"
                  value={formData.image_url}
                  onChange={handleInputChange}
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="is_featured"
                  name="is_featured"
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <Label htmlFor="is_featured" className="cursor-pointer">
                  Mettre en avant (coup de cœur)
                </Label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1">
                  <Save className="w-4 h-4 mr-2" />
                  {editingProduct ? 'Mettre à jour' : 'Créer le produit'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Products List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.length === 0 ? (
          <Card className="col-span-full text-center py-12">
            <CardContent>
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Aucun produit
              </h3>
              <p className="text-gray-600 mb-6">
                Commencez par ajouter votre premier produit
              </p>
              <Button onClick={() => setShowForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un produit
              </Button>
            </CardContent>
          </Card>
        ) : (
          products.map((product) => (
            <Card key={product.id} className="hover:shadow-lg transition-shadow">
              {product.image_url && (
                <div className="h-48 bg-gray-200 overflow-hidden rounded-t-lg">
                  <img 
                    src={product.image_url} 
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <Badge className="bg-blue-100 text-blue-800">
                    {getCategoryLabel(product.category)}
                  </Badge>
                  {product.is_featured && (
                    <Badge className="bg-yellow-100 text-yellow-800">
                      <Star className="w-3 h-3 mr-1" />
                      Coup de cœur
                    </Badge>
                  )}
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {product.name}
                </h3>
                
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                  {product.description}
                </p>

                <div className="flex items-center gap-2 mb-3">
                  {product.original_price && (
                    <span className="text-sm text-gray-400 line-through">
                      {product.original_price.toFixed(2)}€
                    </span>
                  )}
                  <span className="text-xl font-bold text-blue-600">
                    {product.price.toFixed(2)}€
                  </span>
                  {product.original_price && (
                    <Badge className="bg-red-100 text-red-800 text-xs">
                      <TrendingDown className="w-3 h-3 mr-1" />
                      -{Math.round((1 - product.price / product.original_price) * 100)}%
                    </Badge>
                  )}
                </div>

                <div className="flex items-center justify-between mb-4">
                  <span className={`text-xs ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    Stock: {product.stock}
                  </span>
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleEdit(product)}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Modifier
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => handleDelete(product.id, product.name)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default ProductManagement;
